import React from 'react';
import { styled } from '@mui/system';
import { Box, IconButton, Tooltip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { getTokensForChain } from 'utils/tokens';
import { getWalletTokenIcon, getTokenDropdownItems } from 'constants/iconMappings';
import Dropdown from './Dropdown';
import { formatNumber } from 'utils/numbers';

/********************  Styled Components  ********************/
const BalanceBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  border: `1px solid ${theme.palette.border.lighter}`,
  borderRadius: 10,
  overflow: 'hidden',
  marginBottom: theme.spacing(1),
}));

const StyledText = styled('p')(({ theme }) => ({
  color: theme.palette.icon.tertiary,
  fontSize: 14,
  lineHeight: '20px',
  letterSpacing: '0.25px',
  fontWeight: 500,
  margin: 0,
}));

const LabelBox = styled(Box)(() => ({
  display: 'flex',
  fontSize: 16,
  fontWeight: 600,
  alignItems: 'center',
}));

const AddButton = styled(IconButton)(({ theme }) => ({
  width: 24,
  height: 24,
  padding: 4,
  marginRight: 8,
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.border.lighter}`,
  borderRadius: 6,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  '& .MuiSvgIcon-root': {
    fontSize: 14,
    color: theme.palette.text.secondary,
  },
}));

/********************  Main Component  ********************/
const ContractBox = ({ balances, visibility, setWalletSidebarOpen, chainId }) => {
  // Get valid tokens for the current chain - derived from BALANCE_TOKEN_LIST
  const getValidTokensForChain = (chainId) => {
    const tokens = getTokensForChain(chainId);
    return tokens.length > 0 ? tokens : ['ETH', 'WETH']; // Default to ETH/WETH for unknown chains
  };

  // Add token to MetaMask wallet
  const addTokenToWallet = async (token) => {
    // Native tokens (ETH, NRG, APE, etc.) cannot be added - they're already there
    if (token.isNative && !token.addresses?.[chainId]) {
      return;
    }

    const tokenAddress = token.addresses?.[chainId];
    if (!tokenAddress) {
      console.warn(`No address found for ${token.symbol} on chain ${chainId}`);
      return;
    }

    // Use contract symbol if specified (for chains where it differs from display symbol)
    const walletSymbol = token.contractSymbols?.[chainId] || token.symbol;

    // Use chain-specific decimals if available, otherwise fall back to default
    const tokenDecimals = token.chainDecimals?.[chainId] ?? token.decimals ?? 18;

    try {
      await window.ethereum?.request({
        method: 'wallet_watchAsset',
        params: {
          type: 'ERC20',
          options: {
            address: tokenAddress,
            symbol: walletSymbol,
            decimals: tokenDecimals,
            image: token.image || '',
          },
        },
      });
    } catch (error) {
      console.error(`Error adding ${token.symbol} to wallet:`, error);
    }
  };

  // Check if token can be added to wallet (has an address on current chain)
  const canAddToWallet = (token) => {
    // Native tokens without addresses can't be added
    if (token.isNative && !token.addresses?.[chainId]) {
      return false;
    }
    return !!token.addresses?.[chainId];
  };

  // Get token icon using centralized config
  const getTokenIcon = (token) => {
    const symbol = token.symbol;
    const isWrapped = token.isWrapped || symbol.startsWith('W');
    const IconComponent = getWalletTokenIcon(symbol, chainId, isWrapped);
    return <IconComponent width={32} height={32} style={{ marginRight: 16 }} />;
  };

  // Get dropdown items dynamically based on token properties
  const getDropdownList = (token) => {
    return getTokenDropdownItems(token.symbol, token.isNative, token.isWrapped);
  };

  if (!balances?.length) {
    return null;
  }

  const validTokens = getValidTokensForChain(chainId);

  return (
    <div>
      {balances.map((token, index) =>
        validTokens.includes(token.symbol) ? (
          <BalanceBox key={index}>
            <LabelBox>
              {canAddToWallet(token) && (
                <Tooltip
                  title={`Add ${token.symbol} to wallet`}
                  placement="top"
                  slotProps={{ popper: { style: { zIndex: 2200 } } }}
                >
                  <AddButton onClick={() => addTokenToWallet(token)} size="small">
                    <AddIcon />
                  </AddButton>
                </Tooltip>
              )}
              {getTokenIcon(token)}
              <Box>
                {token.symbol}
                <StyledText>{token.name}</StyledText>
              </Box>
            </LabelBox>
            <LabelBox>
              <Box style={{ marginRight: 8, textAlign: 'end' }}>
                {visibility ? formatNumber(token.balance) : '*****'}
                <StyledText>{visibility ? `$ ${formatNumber(token.value)}` : '*****'}</StyledText>
              </Box>
              <Dropdown
                symbol={token.symbol}
                list={getDropdownList(token)}
                setWalletSidebarOpen={setWalletSidebarOpen}
              />
            </LabelBox>
          </BalanceBox>
        ) : (
          ''
        ),
      )}
    </div>
  );
};

export default ContractBox;
